import React, { useState } from "react";
import styles from "./video_chat.module.scss";
import { FormControl, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { APIConstant } from "services/api-constant";
import axios from "axios";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router";

import { apiBaseUrl } from '../../../../services/config';
const API_BASE = apiBaseUrl;
// ── Theme tokens ──────────────────────────────────────────────────
const T = {
  bg:     "#06101F",
  card:   "#0D1B2E",
  card2:  "#0A1628",
  border: "rgba(0,200,255,0.10)",
  borderH:"rgba(0,200,255,0.20)",
  cyan:   "#00C8FF",
  purple: "#7C5CFF",
  green:  "#00E5A3",
  amber:  "#FFB800",
  red:    "#FF4D6D",
  text:   "#E8F0FE",
  muted:  "#7A93B4",
  dim:    "#3D5473",
  input:  "rgba(255,255,255,0.05)",
};

const EvaluationSchema = Yup.object().shape({
  status:             Yup.string().required("Interview status is required"),
  interviewerFeedback:Yup.string().trim().required("Feedback is required"),
  interviewerScore:   Yup.string().nullable(),
});

// ── Shared input style ────────────────────────────────────────────
const inputSx = {
  width: "100%", padding: "8px 12px",
  background: T.input, border: `1px solid ${T.border}`,
  borderRadius: 7, color: T.text,
  fontFamily: "Outfit,sans-serif", fontSize: 13,
  outline: "none", boxSizing: "border-box",
};

const InterviewEvaluation = ({
  handleSuccessPopup, questionLen, evaluations,
  interviewId, handleCloseEvaluation, fullTranscript, setInterviewerStatus,
}) => {
  const navigate = useNavigate();

  // ── Aggregations (unchanged logic) ───────────────────────────
  const count          = evaluations.length;
  const totalConfidence= evaluations.reduce((s,q)=>s+Number(q.confidence||0),0);
  const avgConfidence  = count ? Math.round(totalConfidence/count) : 0;

  const emotionCounts  = evaluations.reduce((acc,q)=>{
    const e=q.emotion||"Unknown"; acc[e]=(acc[e]||0)+1; return acc;
  },{});
  const majorityEmotion= Object.entries(emotionCounts).sort((a,b)=>b[1]-a[1])[0]?.[0]||"N/A";

  const sentimentCounts= evaluations.reduce((acc,q)=>{
    const s=q.sentiment||"Unknown"; acc[s]=(acc[s]||0)+1; return acc;
  },{});
  const majoritySentiment=Object.entries(sentimentCounts).sort((a,b)=>b[1]-a[1])[0]?.[0]||"N/A";

  const results        = evaluations.map(q=>({
    ...q,
    evaluationVal: q.similarity>0.7?"Strong answer.":"Try aligning more with the key concepts."
  }));
  const counts         = results.reduce((acc,r)=>{
    acc[r.evaluationVal]=(acc[r.evaluationVal]||0)+1; return acc;
  },{});
  const majorityEvaluation = Object.entries(counts).length
    ? Object.entries(counts).reduce((a,b)=>b[1]>a[1]?b:a)[0]
    : "—";
  const strongCount    = results.filter(r=>r.evaluationVal==="Strong answer.").length;
  const scorePercentage= ((strongCount/Math.max(count,1))*100).toFixed(2);

  const buildAnswersObject = () =>
    evaluations.reduce((acc,q)=>{
      acc[q.text||"Untitled"]={
        feedback:q.feedback||"", sentiment:q.sentiment||"",
        confidence:q.confidence||"", emotion:q.emotion||"", evaluation:q.evaluation||"",
      }; return acc;
    },{});

  const buildTranscriptsObject = () =>
    evaluations.reduce((acc,q)=>{
      acc[q.text||"Untitled"]={
        questions:q.text||"", answers:q.transcript||"", time:q.time||"",
      }; return acc;
    },{});

  const saveEvaluation = async (payload) => {
    setInterviewerStatus(payload.status);
    try {
      // Use interviews-v2 endpoint (new system), not legacy interviews_new
      
      // video_chat opens in a new tab — sessionStorage is tab-scoped,
      // so fall back to localStorage, then URL param
      const urlParams = new URLSearchParams(window.location.search);
      const token =
        sessionStorage.getItem("platform_token") ||
        localStorage.getItem("platform_token")   ||
        urlParams.get("token")                   || "";
      await axios.put(
        `${API_BASE}/interviews-v2/${interviewId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      handleCloseEvaluation();
      handleSuccessPopup();
    } catch(err) {
      console.error("Evaluation save error:", err);
      alert("Error while submitting evaluation.");
    }
  };

  // ── Stat row component ────────────────────────────────────────
  const StatRow = ({ label, value, color }) => (
    <div style={{ display:"flex", justifyContent:"space-between",
      alignItems:"center", padding:"10px 0",
      borderBottom:`1px solid ${T.border}` }}>
      <span style={{ fontSize:13, color:T.muted }}>{label}</span>
      <span style={{ fontSize:12, fontWeight:700, padding:"3px 12px",
        borderRadius:100, background:`${color||T.cyan}18`,
        color:color||T.cyan, border:`1px solid ${color||T.cyan}30` }}>
        {value}
      </span>
    </div>
  );

  return (
    <div style={{ background:T.card, borderRadius:14, overflow:"hidden",
      width:"min(90vw,860px)", fontFamily:"Outfit,sans-serif" }}>

      {/* Header */}
      <div style={{ padding:"18px 24px", background:T.card2,
        borderBottom:`1px solid ${T.border}`,
        display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div>
          <div style={{ fontSize:11, color:T.cyan, fontWeight:700,
            letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:2 }}>
            INTERVIEW COMPLETE
          </div>
          <h3 style={{ margin:0, fontSize:16, fontWeight:800, color:T.text }}>
            Evaluation Summary
          </h3>
        </div>
        {/* AI Score badge */}
        <div style={{ padding:"8px 20px", borderRadius:10,
          border:`2px solid ${T.amber}`, background:`${T.amber}10`,
          textAlign:"center" }}>
          <div style={{ fontSize:11, color:T.amber, fontWeight:700,
            letterSpacing:"0.08em" }}>AI SCORE</div>
          <div style={{ fontSize:22, fontWeight:900, color:T.amber }}>
            {scorePercentage}%
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr",
        gap:0, maxHeight:"70vh", overflowY:"auto" }}>

        {/* ── Left: AI Evaluation ── */}
        <div style={{ padding:"20px 24px",
          borderRight:`1px solid ${T.border}` }}>
          <div style={{ fontSize:11, color:T.cyan, fontWeight:700,
            letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:14 }}>
            🤖 AI Evaluation
          </div>

          <StatRow label="Confidence Level" value={avgConfidence}          color={T.cyan}/>
          <StatRow label="Emotion"          value={majorityEmotion}        color={T.purple}/>
          <StatRow label="Engagement Score" value={majoritySentiment}      color={T.green}/>
          <StatRow label="Evaluation"
            value={majorityEvaluation==="Strong answer."?"Strong ✓":"Needs Work"}
            color={majorityEvaluation==="Strong answer."?T.green:T.amber}/>

          {/* Per-question breakdown */}
          {evaluations.length > 0 && (
            <div style={{ marginTop:18 }}>
              <div style={{ fontSize:11, color:T.dim, fontWeight:700,
                textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:10 }}>
                Per Question
              </div>
              {evaluations.map((q,i)=>(
                <div key={i} style={{ marginBottom:10, padding:"10px 12px",
                  background:T.input, borderRadius:8,
                  border:`1px solid ${T.border}` }}>
                  <div style={{ fontSize:12, color:T.text, fontWeight:600,
                    marginBottom:4 }}>
                    Q{i+1}: {(q.text||"").slice(0,60)}{(q.text||"").length>60?"…":""}
                  </div>
                  {q.feedback && (
                    <div style={{ fontSize:11.5, color:T.muted, lineHeight:1.5 }}>
                      {q.feedback}
                    </div>
                  )}
                  {q.confidence !== undefined && (
                    <div style={{ fontSize:11, color:T.dim, marginTop:4 }}>
                      Confidence: {q.confidence} · {q.emotion||"—"}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Right: Interviewer Evaluation form ── */}
        <div style={{ padding:"20px 24px" }}>
          <div style={{ fontSize:11, color:T.green, fontWeight:700,
            letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:14 }}>
            🎙️ Interviewer Evaluation
          </div>

          <Formik
            initialValues={{ interviewerScore:"", status:"", interviewerFeedback:"" }}
            validationSchema={EvaluationSchema}
            onSubmit={(values)=>{
              saveEvaluation({
                status:           values.status,
                score:            scorePercentage,
                feedback:         values.interviewerFeedback,
                sentiment:        majoritySentiment,
                confidence:       avgConfidence,
                evaluation:       majorityEvaluation,
                emotion:          majorityEmotion,
                transcript:       JSON.stringify(buildTranscriptsObject()),
                answers:          JSON.stringify(buildAnswersObject()),
                interviewerScore: values.interviewerScore,
              });
            }}>
            {({ values, errors, touched, handleChange, handleBlur })=>(
              <Form>

                {/* Number of Questions (read-only) */}
                <div style={{ display:"flex", justifyContent:"space-between",
                  alignItems:"center", padding:"10px 0",
                  borderBottom:`1px solid ${T.border}`, marginBottom:12 }}>
                  <span style={{ fontSize:13, color:T.muted }}>Number of Questions</span>
                  <span style={{ fontSize:14, fontWeight:800, color:T.cyan }}>{questionLen}</span>
                </div>

                {/* Correct Answers */}
                <div style={{ marginBottom:14 }}>
                  <label style={{ fontSize:11, fontWeight:700, color:T.muted,
                    textTransform:"uppercase", letterSpacing:"0.07em",
                    display:"block", marginBottom:5 }}>
                    Correct Answers
                  </label>
                  <input name="interviewerScore" type="text"
                    value={values.interviewerScore}
                    onChange={handleChange} onBlur={handleBlur}
                    style={inputSx} placeholder="e.g. 4"/>
                </div>

                {/* Status */}
                <div style={{ marginBottom:14 }}>
                  <label style={{ fontSize:11, fontWeight:700, color:T.muted,
                    textTransform:"uppercase", letterSpacing:"0.07em",
                    display:"block", marginBottom:8 }}>
                    Status *
                  </label>
                  <FormControl>
                    <RadioGroup row name="status"
                      value={values.status}
                      onChange={handleChange} onBlur={handleBlur}>
                      {[
                        {val:"SELECTED",     label:"Selected",     color:T.green},
                        {val:"NOT-SELECTED", label:"Not Selected", color:T.red},
                        {val:"ONHOLD",       label:"On Hold",      color:T.amber},
                      ].map(opt=>(
                        <FormControlLabel key={opt.val} value={opt.val}
                          label={<span style={{ fontSize:13, color:T.muted }}>{opt.label}</span>}
                          control={<Radio size="small" sx={{
                            color:T.dim,
                            "&.Mui-checked":{ color:opt.color },
                          }}/>}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                  {touched.status && errors.status && (
                    <div style={{ color:T.red, fontSize:11, marginTop:4 }}>
                      {errors.status}
                    </div>
                  )}
                </div>

                {/* Interview Score */}
                <div style={{ marginBottom:14 }}>
                  <label style={{ fontSize:11, fontWeight:700, color:T.muted,
                    textTransform:"uppercase", letterSpacing:"0.07em",
                    display:"block", marginBottom:5 }}>
                    Interview Score
                  </label>
                  <input name="interviewerScore" type="text"
                    value={values.interviewerScore}
                    onChange={handleChange} onBlur={handleBlur}
                    style={inputSx} placeholder="e.g. 8/10"/>
                </div>

                {/* Feedback */}
                <div style={{ marginBottom:16 }}>
                  <label style={{ fontSize:11, fontWeight:700, color:T.muted,
                    textTransform:"uppercase", letterSpacing:"0.07em",
                    display:"block", marginBottom:5 }}>
                    Feedback *
                  </label>
                  <textarea name="interviewerFeedback"
                    value={values.interviewerFeedback}
                    onChange={handleChange} onBlur={handleBlur}
                    rows={4}
                    style={{ ...inputSx, resize:"vertical", lineHeight:1.6 }}
                    placeholder="Overall feedback about the candidate..."/>
                  {touched.interviewerFeedback && errors.interviewerFeedback && (
                    <div style={{ color:T.red, fontSize:11, marginTop:4 }}>
                      {errors.interviewerFeedback}
                    </div>
                  )}
                </div>

                {/* Submit */}
                <button type="submit"
                  style={{ width:"100%", padding:"10px",
                    borderRadius:9, border:"none",
                    background:`linear-gradient(135deg,${T.cyan},${T.purple})`,
                    color:"#06101F", fontFamily:"Outfit,sans-serif",
                    fontSize:13, fontWeight:800, cursor:"pointer",
                    boxShadow:`0 4px 16px rgba(0,200,255,0.25)` }}>
                  Submit Evaluation
                </button>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default InterviewEvaluation;